import { useMutation, UseMutationResult } from '@tanstack/react-query'
import { useApiWithIDToken } from '../../state/modules/common'
import { useNotifications } from '../../state/modules/notifications'
import { Variant } from '../../common/types'
import { useProgressToast } from './helpers/useProgressToast'

type DownloadSiteRFPDocumentDirectoryVariables = {
  siteRFPId: number
  siteRFPName: string
}

/**
 * Download a zip file of all versions of all construction documents for a given site RFP.
 * @returns A UseMutationResult object for triggering the download.
 */
export const useDownloadSiteRFPDocumentDirectory = (): UseMutationResult<
  void,
  Error,
  DownloadSiteRFPDocumentDirectoryVariables
> => {
  const api = useApiWithIDToken()
  const { send: sendNotification } = useNotifications()
  const { startProgress, endProgress } = useProgressToast()

  return useMutation<
    void,
    Error,
    DownloadSiteRFPDocumentDirectoryVariables,
    { toastId: string }
  >({
    mutationFn: ({ siteRFPId, siteRFPName }) =>
      downloadSiteRFPDocumentDirectory(api, siteRFPId, siteRFPName),
    onMutate: (vars) => ({
      toastId: startProgress(
        'Downloading Documents',
        `${vars.siteRFPName} Documents`
      ),
    }),
    onSuccess: () => {
      sendNotification({
        title: 'Download Successful',
        message: `All documents have been successfully downloaded.`,
        useRucksackUI: true,
      })
    },
    onError: () => {
      sendNotification({
        title: 'Download Failed',
        message: `An error occurred while trying to download all documents. Please try again.`,
        variant: Variant.DANGER,
        useRucksackUI: true,
      })
    },
    onSettled: (_data, _error, _vars, context) => {
      endProgress(context?.toastId)
    },
  })
}

async function downloadSiteRFPDocumentDirectory(
  api: ReturnType<typeof useApiWithIDToken>,
  siteRFPId: number,
  siteRFPName: string
): Promise<void> {
  try {
    const response = await api.get<Blob>(
      `/site-rfps/${siteRFPId}/document-directory`,
      {
        responseType: 'blob',
      }
    )

    // Create a URL for the blob and trigger a download.
    const blob = new Blob([response.data], { type: 'application/zip' })
    const downloadUrl = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = downloadUrl
    const fileName = `${siteRFPName} Documents.zip`
    link.setAttribute('download', fileName)
    document.body.appendChild(link)
    link.click()

    // Clean up the blob URL.
    window.URL.revokeObjectURL(downloadUrl)
    link.remove()
  } catch (error) {
    throw new Error(
      `Failed to download document directory for site RFP ${siteRFPId}: ${
        error instanceof Error ? error.message : 'Unknown error'
      }`
    )
  }
}
